import { useState, useEffect } from 'react'
import { Link, useLocation } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import type { LucideIcon } from 'lucide-react'
import {
  LayoutDashboard,
  CreditCard,
  Settings,
  Package,
  BarChart3,
  UserCog,
  ChevronLeft,
  ChevronRight,
  Warehouse,
  Receipt,
  FileWarning,
  Store,
  Lock,
  Contact,
  BadgePercent,
  Megaphone,
  Inbox,
  MapPin,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { UserMenu } from '@/components/ui/user-menu'
import type { User as UserType } from '@/types'
import apiClient from '@/api/client'
import { useOnlineOrderingEnabled } from '@/hooks/useOnlineOrderingEnabled'
import { useModuleVisibility } from '@/hooks/useModuleVisibility'
import { isOptionalModule } from '@/lib/modules'
import { marketingLandingPath, navSectionIdsForRole } from '@/lib/staff-roles'
import { cn } from '@/lib/utils'

export const MOBILE_ADMIN_HEADER_HEIGHT_CLASS = 'h-14'

/** Collapsed desktop rail — base 4.625rem + 10% for logo + centered icons */
const COLLAPSED_RAIL_WIDTH_CLASS = 'w-[calc(4.625rem*1.1)]'

type AdminSectionDef = {
  id: string
  label: string
  icon: LucideIcon
  description: string
  href: string
}

const adminSections: AdminSectionDef[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    description: 'Overview and statistics',
    href: '/admin/dashboard',
  },
  {
    id: 'counter',
    label: 'Point of Sale (POS)',
    icon: CreditCard,
    description: 'Payment processing',
    href: '/admin/counter',
  },
  {
    id: 'online-orders',
    label: 'Online Orders',
    icon: Inbox,
    description: 'Incoming online orders to accept or reject',
    href: '/admin/online-orders',
  },
  {
    id: 'delivery-zones',
    label: 'Delivery Zones',
    icon: MapPin,
    description: 'Delivery areas, charges, and minimums for online orders',
    href: '/admin/delivery-zones',
  },
  {
    id: 'day-close',
    label: 'Daily Closing',
    icon: Lock,
    description: 'Start the day, count cash, finish the day — every day',
    href: '/admin/day-close',
  },
  {
    id: 'inventory',
    label: 'Store Inventory',
    icon: Warehouse,
    description: 'Supplies & stock management',
    href: '/admin/inventory',
  },
  {
    id: 'catalog',
    label: 'Manage Catalog',
    icon: Package,
    description: 'Categories and products',
    href: '/admin/catalog',
  },
  {
    id: 'staff',
    label: 'Manage Staff',
    icon: UserCog,
    description: 'User and role management',
    href: '/admin/staff',
  },
  {
    id: 'customers',
    label: 'Customers',
    icon: Contact,
    description: 'Customer directory, profiles, and merge',
    href: '/admin/customers',
  },
  // Bank card promos live inside the Marketing Suite as a tab — see
  // marketing/AdminCardPromos.tsx. Managers get this section for that one
  // tab; label/href below are rewritten for them so it doesn't read as
  // access to the whole suite.
  {
    id: 'marketing',
    label: 'Marketing Suite',
    icon: Megaphone,
    description: 'Segments, promo codes, loyalty, campaigns, QR codes, and bank card promos',
    href: '/admin/marketing/overview',
  },
  {
    id: 'reports',
    label: 'View Reports',
    icon: BarChart3,
    description: 'Analytics and reports',
    href: '/admin/reports',
  },
  {
    id: 'expenses',
    label: 'View Expenses',
    icon: Receipt,
    description: 'Expense tracking and P&L',
    href: '/admin/expenses',
  },
  {
    id: 'void-log',
    label: 'Void Log',
    icon: FileWarning,
    description: 'Voided items audit trail',
    href: '/admin/void-log',
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    description: 'System configuration',
    href: '/admin/settings',
  },
]

/**
 * Tiny pill rendered on the Day Close sidebar item so managers always
 * know whether a business day is currently open / staged / closed /
 * reopened without having to navigate to the page.
 */
function renderDayClosePill(
  state: 'open' | 'staged' | 'closed' | 'reopened' | undefined,
) {
  if (!state) {
    // No day open — most urgent state, draws the eye.
    return (
      <Badge
        variant="outline"
        className="ml-2 border-rose-300 bg-rose-50 px-1.5 py-0 text-[9px] font-bold uppercase tracking-wide text-rose-700 dark:border-rose-800 dark:bg-rose-950/60 dark:text-rose-300"
      >
        Closed
      </Badge>
    )
  }
  if (state === 'staged') {
    return (
      <Badge
        variant="outline"
        className="ml-2 border-amber-300 bg-amber-50 px-1.5 py-0 text-[9px] font-bold uppercase tracking-wide text-amber-700 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-300"
      >
        Staged
      </Badge>
    )
  }
  if (state === 'closed') {
    return (
      <Badge
        variant="outline"
        className="ml-2 border-muted bg-muted px-1.5 py-0 text-[9px] font-bold uppercase tracking-wide text-muted-foreground"
      >
        Sealed
      </Badge>
    )
  }
  if (state === 'reopened') {
    // Was sealed, now open again because a sale arrived after close.
    // Amber draws attention — owner should re-run End-the-day to relock.
    return (
      <Badge
        variant="outline"
        className="ml-2 border-amber-300 bg-amber-50 px-1.5 py-0 text-[9px] font-bold uppercase tracking-wide text-amber-700 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-300"
      >
        Reopened
      </Badge>
    )
  }
  return (
    <Badge
      variant="outline"
      className="ml-2 border-emerald-300 bg-emerald-50 px-1.5 py-0 text-[9px] font-bold uppercase tracking-wide text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300"
    >
      Live
    </Badge>
  )
}

/** Longest-prefix match for sidebar label; used by mobile shell title. */
export function adminNavTitleFromPath(pathname: string): string {
  let best = 'POS Admin'
  let bestLen = -1
  for (const s of adminSections) {
    if (pathname === s.href || pathname.startsWith(`${s.href}/`)) {
      if (s.href.length > bestLen) {
        bestLen = s.href.length
        best = s.label
      }
    }
  }
  return best
}

interface AdminSidebarProps {
  user: UserType
  /** Viewport &lt; lg — parent drives drawer open state */
  isNarrowViewport: boolean
  narrowDrawerOpen: boolean
  onNarrowDrawerOpenChange: (open: boolean) => void
}

export function AdminSidebar({
  user,
  isNarrowViewport,
  narrowDrawerOpen,
  onNarrowDrawerOpenChange,
}: AdminSidebarProps) {
  // Tablets (768-1023) start with the icon rail so content keeps its width;
  // desktop starts expanded. User toggle still wins after mount.
  const [desktopCollapsed, setDesktopCollapsed] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 768px) and (max-width: 1023px)').matches
  )
  const [, setIsMobile] = useState(false)
  const [isTablet, setIsTablet] = useState(false)
  const location = useLocation()

  const { data: settingsRes } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: 1000 * 60 * 5,
  })
  // Live pill on the Day Close item — keeps managers aware of "no day
  // open" or "close staged" states even when they're on another screen.
  const showDayClosePill = user.role === 'admin' || user.role === 'manager'
  const { data: dayOpsCurrent } = useQuery({
    queryKey: ['day-ops', 'current'],
    queryFn: () => apiClient.getDayOpsCurrent(),
    enabled: showDayClosePill,
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
  const dayOpsState = dayOpsCurrent?.data?.status as
    | 'open'
    | 'staged'
    | 'closed'
    | 'reopened'
    | undefined

  const logoUrl =
    ((settingsRes?.data as Record<string, unknown>)?.receipt_logo_url as string) ||
    ''

  const allowedIds = navSectionIdsForRole(user.role)
  // Optional modules stay out of the nav until settings confirm this
  // deployment shows them (ENABLED_MODULES) — no flash on a trimmed install.
  const modules = useModuleVisibility()
  const visibleAdminSections = adminSections
    .filter((s) => {
      if (allowedIds !== null && !allowedIds.has(s.id)) return false
      if (isOptionalModule(s.id) && !modules.isEnabled(s.id)) return false
      return true
    })
    // A manager's only marketing surface is the Bank card promos tab, so the
    // nav item names that instead of the suite it can't otherwise open.
    .map((s) =>
      s.id === 'marketing' && user.role === 'manager'
        ? {
            ...s,
            label: 'Bank Card Promos',
            icon: BadgePercent,
            description: 'Card-payment discounts reimbursed by banks (HBL, Meezan, …)',
            href: marketingLandingPath(user.role),
          }
        : s,
    )

  // Live count of pending online orders for the "Online Orders" nav badge.
  // Only polled for roles that can see the section (avoids 401 noise) AND on
  // tenants with the premium add-on (avoids 403 noise — locked tenants show a
  // golden lock instead; the nav item stays visible as the upsell surface).
  const canSeeOnlineOrders =
    (allowedIds === null || allowedIds.has('online-orders')) &&
    modules.isEnabled('online-orders')
  const { enabled: onlineOrderingOn, isLoading: onlineOrderingLoading } =
    useOnlineOrderingEnabled()
  const { data: pendingOnlineOrders } = useQuery({
    queryKey: ['online-orders', 'pending'],
    queryFn: () => apiClient.getOnlineOrders().then((r) => r.data ?? []),
    enabled: canSeeOnlineOrders && onlineOrderingOn,
    refetchInterval: 15_000,
  })
  const onlineOrdersCount = pendingOnlineOrders?.length ?? 0
  // Nothing renders while the gate resolves — no lock/badge flash either way.
  const showOnlineOrdersLock = !onlineOrderingOn && !onlineOrderingLoading

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth
      setIsMobile(width < 768)
      setIsTablet(width >= 768 && width < 1024)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  const isActiveRoute = (href: string) => {
    if (location.pathname === href) return true
    // Marketing has sub-routes (/admin/marketing/promo-codes,
    // /admin/marketing/loyalty, …) and we want the parent nav item to
    // stay highlighted across them. Generalise: any section href under
    // /admin/marketing/ counts as a marketing route.
    if (href.startsWith('/admin/marketing/') && location.pathname.startsWith('/admin/marketing/')) {
      return true
    }
    return false
  }

  const showNarrowDrawer = isNarrowViewport && narrowDrawerOpen
  const sidebarFullContent = isNarrowViewport ? showNarrowDrawer : !desktopCollapsed

  const closeNarrowDrawer = () => onNarrowDrawerOpenChange(false)

  const toggleDesktop = () => setDesktopCollapsed((c) => !c)

  const desktopRailCollapsed = !isNarrowViewport && desktopCollapsed
  const navIconClass =
    desktopRailCollapsed ? 'size-6' : 'size-5'

  const sidebarExpanded =
    isNarrowViewport ? showNarrowDrawer : !desktopCollapsed

  const toggleAriaLabel = isNarrowViewport
    ? 'Close navigation menu'
    : desktopCollapsed
      ? 'Expand sidebar'
      : 'Collapse sidebar'

  const ToggleIcon = isNarrowViewport || !desktopCollapsed ? ChevronLeft : ChevronRight

  const logoImgClass = cn(
    'object-contain',
    desktopRailCollapsed
      ? 'w-full max-h-[4.75rem] px-1.5 py-2'
      // Cap the height so a square/tall logo doesn't eat the sidebar — the old
      // `w-full h-auto` rendered a square logo ~256px tall and swallowed most
      // of the panel in landscape. max-h is in rem so it tracks the UI scale.
      : 'mx-auto h-auto max-h-16 w-auto'
  )

  const fallbackStoreClass = cn(
    'flex w-full items-center justify-center bg-primary',
    desktopRailCollapsed ? 'h-14' : 'h-12'
  )

  const fallbackStoreIconClass = cn(
    'text-primary-foreground',
    desktopRailCollapsed ? 'size-8' : 'size-6'
  )

  /** Desktop: toggle below logo (saves horizontal space); drawer: logo + close in one row */
  const stackToggleBelowBrand = !isNarrowViewport

  return (
    <>
      {isNarrowViewport && showNarrowDrawer && (
        <div
          className="fixed inset-x-0 bottom-0 top-14 z-[50] bg-black/50 md:hidden"
          aria-hidden
          onClick={closeNarrowDrawer}
        />
      )}

      <div
        className={`flex flex-col border-r border-border bg-card transition-[transform,width] duration-300 ease-out ${
          isNarrowViewport
            ? `fixed bottom-0 left-0 top-14 z-[55] max-h-[calc(100dvh-3.5rem)] w-80 max-w-[min(100vw,20rem)] md:hidden ${
                showNarrowDrawer
                  ? 'translate-x-0'
                  : 'pointer-events-none -translate-x-full'
              }`
            : `relative z-30 h-dvh min-h-0 ${desktopCollapsed ? COLLAPSED_RAIL_WIDTH_CLASS : 'w-64'}`
        }`}
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <div
            className={cn(
              'border-b border-border px-3 pb-2 pt-[max(0.75rem,env(safe-area-inset-top,0px))] lg:pt-4',
              stackToggleBelowBrand
                ? 'flex flex-col items-stretch gap-1'
                : 'flex flex-row items-center gap-2'
            )}
          >
            <div
              className={cn(
                'min-w-0',
                stackToggleBelowBrand ? 'w-full' : 'min-w-0 flex-1'
              )}
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Store logo"
                  className={logoImgClass}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                    const parent = e.currentTarget.parentElement
                    if (parent) {
                      parent.innerHTML =
                        '<div class="flex h-12 w-full items-center justify-center bg-primary"><svg class="size-6 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg></div>'
                    }
                  }}
                />
              ) : (
                <div className={fallbackStoreClass}>
                  <Store className={fallbackStoreIconClass} />
                </div>
              )}
            </div>

            <div
              className={cn(
                stackToggleBelowBrand && 'w-full',
                !stackToggleBelowBrand && 'shrink-0'
              )}
            >
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={toggleAriaLabel}
                aria-expanded={sidebarExpanded}
                aria-controls="admin-sidebar-nav"
                onClick={() =>
                  isNarrowViewport ? closeNarrowDrawer() : toggleDesktop()
                }
                className={cn(
                  'touch-manipulation',
                  stackToggleBelowBrand
                    ? 'h-auto min-h-9 w-full shrink-0 py-2.5 text-foreground'
                    : 'h-11 w-11 shrink-0'
                )}
              >
                <ToggleIcon
                  className="size-5"
                  aria-hidden
                />
              </Button>
            </div>
          </div>

          <div
            id="admin-sidebar-nav"
            className={cn(
              'min-h-0 flex-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]',
              desktopRailCollapsed
                ? 'space-y-2 px-1.5 py-4'
                : 'space-y-2 p-4'
            )}
          >
            {visibleAdminSections.map((section) => {
              const Icon = section.icon
              const dayClosePill =
                section.id === 'day-close' && showDayClosePill
                  ? renderDayClosePill(dayOpsState)
                  : null
              const onlineOrdersBadge =
                section.id === 'online-orders' ? (
                  showOnlineOrdersLock ? (
                    <Lock
                      className="ml-2 h-3.5 w-3.5 shrink-0 text-amber-500"
                      aria-label="Premium add-on"
                    />
                  ) : onlineOrdersCount > 0 ? (
                    <Badge className="ml-2 bg-red-600 px-1.5 py-0 text-[10px] font-bold text-white hover:bg-red-600">
                      {onlineOrdersCount}
                    </Badge>
                  ) : null
                ) : section.id === 'delivery-zones' && showOnlineOrdersLock ? (
                  // Delivery zones ride the same online-ordering add-on gate.
                  <Lock
                    className="ml-2 h-3.5 w-3.5 shrink-0 text-amber-500"
                    aria-label="Premium add-on"
                  />
                ) : null
              return (
                <Tooltip key={section.id} delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Link to={section.href} className="block">
                      <Button
                        variant={isActiveRoute(section.href) ? 'default' : 'ghost'}
                        className={cn(
                          'w-full touch-manipulation transition-colors',
                          desktopRailCollapsed
                            ? 'justify-center gap-0 px-0'
                            : 'justify-start px-4',
                          isTablet ? 'h-12 text-base' : 'h-11 text-sm'
                        )}
                        onClick={() => {
                          if (isNarrowViewport) closeNarrowDrawer()
                        }}
                      >
                        {section.id === 'online-orders' &&
                        !sidebarFullContent &&
                        (showOnlineOrdersLock || onlineOrdersCount > 0) ? (
                          <span className="relative inline-flex">
                            <Icon className={cn('shrink-0', navIconClass)} aria-hidden />
                            {showOnlineOrdersLock ? (
                              <Lock
                                className="absolute -right-1.5 -top-1.5 h-3.5 w-3.5 rounded-full bg-card p-0.5 text-amber-500"
                                aria-label="Premium add-on"
                              />
                            ) : (
                              <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[9px] font-bold leading-none text-white motion-safe:animate-pulse">
                                {onlineOrdersCount > 9 ? '9+' : onlineOrdersCount}
                              </span>
                            )}
                          </span>
                        ) : (
                          <Icon className={cn('shrink-0', navIconClass)} aria-hidden />
                        )}
                        {sidebarFullContent && (
                          <span className="ml-3 flex-1 text-left">{section.label}</span>
                        )}
                        {sidebarFullContent && dayClosePill}
                        {sidebarFullContent && onlineOrdersBadge}
                      </Button>
                    </Link>
                  </TooltipTrigger>
                  {/* Instant custom label — only when the rail is collapsed
                      (when expanded the label already shows inline). */}
                  {!sidebarFullContent && (
                    <TooltipContent
                      side="right"
                      sideOffset={10}
                      className="rounded-full border-transparent bg-neutral-900 px-3 py-1.5 text-xs font-medium text-white shadow-lg duration-0 animate-none"
                    >
                      {section.label}
                    </TooltipContent>
                  )}
                </Tooltip>
              )
            })}
          </div>

          <div
            className={cn(
              'border-t border-border pb-[max(1rem,env(safe-area-inset-bottom,0px))]',
              desktopRailCollapsed
                ? 'flex justify-center px-2 pt-3'
                : 'p-4'
            )}
          >
            <UserMenu
              user={user}
              collapsed={desktopRailCollapsed}
              size={isTablet ? 'lg' : 'md'}
            />
          </div>
        </div>
      </div>
    </>
  )
}
